/**
 * ProductionOperatorDashboard — Production Operator (shop floor IC).
 *
 * Ported from `ProdOpDashboard` in aura-exec.jsx.
 * Today's shift production orders + work-centre strip + QC checkpoints +
 * raw material request + barcode + FG activation + shift report.
 *
 * UI-only. Tenant-safe (product SKUs come from DB — no tenant hardcoding).
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ScanLine, Users, ClipboardCheck } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { safeGet, formatNum, todayLabel } from './_execShared';

// ─── Types ────────────────────────────────────────────────────────────────

interface ShiftWorkOrder {
  id: string;
  orderNumber?: string;
  productName?: string;
  product?: { name?: string };
  sku?: string;
  quantity?: number;
  completedQty?: number;
  done?: number;
  workstation?: { name?: string } | string;
  dueDate?: string;
  dueLabel?: string;
  priority?: 'high' | 'med' | 'medium' | 'low';
}

interface QcRow {
  id: string;
  workOrder?: string;
  wo?: string;
  sku?: string;
  productName?: string;
  unitsPending?: number;
  count?: number;
  inspector?: { firstName?: string; lastName?: string; name?: string };
  insp?: string;
}

interface RmRequest {
  id: string;
  material?: string;
  qty?: number;
  status?: string;
  createdAt?: string;
}

interface ShiftStats {
  unitsDone?: number;
  unitsTarget?: number;
  qcFirstPassPct?: number;
  oee?: number;
  incidents?: number;
  idleMinutes?: number;
}

// ─── Atoms ────────────────────────────────────────────────────────────────

function Skeleton({ h = 60 }: { h?: number }) {
  return (
    <div
      style={{
        height: h,
        background: 'var(--ivory-2)',
        borderRadius: 6,
        animation: 'pulse 1.5s ease-in-out infinite',
      }}
    />
  );
}

function NoData({ label = 'No data yet' }: { label?: string }) {
  return <div style={{ fontSize: 12, color: 'var(--ink-3)', padding: '8px 0' }}>{label}</div>;
}

function Chip({
  children,
  color = 'var(--ink-3)',
  bg = 'var(--ivory-2)',
}: {
  children: React.ReactNode;
  color?: string;
  bg?: string;
}) {
  return (
    <span
      style={{
        fontSize: 10,
        padding: '2px 7px',
        borderRadius: 10,
        background: bg,
        color,
        fontWeight: 500,
      }}
    >
      {children}
    </span>
  );
}

function Progress({ value, color = 'var(--saffron)', h = 3 }: { value: number; color?: string; h?: number }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  return (
    <div style={{ height: h, background: 'var(--ivory-3)', borderRadius: h, flex: 1 }}>
      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: h }} />
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────

export function ProductionOperatorDashboard({
  onSeeTeam,
}: {
  onSeeTeam?: () => void;
} = {}) {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const firstName = user?.firstName ?? 'there';

  const [wos, setWos] = useState<ShiftWorkOrder[]>([]);
  const [wosLoading, setWosLoading] = useState(true);
  const [wosErr, setWosErr] = useState<string | null>(null);

  const [qcPending, setQcPending] = useState<QcRow[]>([]);
  const [qcLoading, setQcLoading] = useState(true);

  const [rmRequests, setRmRequests] = useState<RmRequest[]>([]);
  const [rmLoading, setRmLoading] = useState(true);

  const [stats, setStats] = useState<ShiftStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    safeGet<{ items?: ShiftWorkOrder[]; workOrders?: ShiftWorkOrder[] } | ShiftWorkOrder[]>(
      '/manufacturing/shift-control/today?operatorId=me',
    ).then((r) => {
      const raw = r.data;
      const parsed = Array.isArray(raw)
        ? raw
        : (raw as { items?: ShiftWorkOrder[]; workOrders?: ShiftWorkOrder[] } | null)?.items ??
          (raw as { workOrders?: ShiftWorkOrder[] } | null)?.workOrders ??
          [];
      setWos(parsed);
      setWosErr(r.error);
      setWosLoading(false);
    });

    safeGet<{ items?: QcRow[] } | QcRow[]>('/manufacturing/qc/pending?operatorId=me').then((r) => {
      const raw = r.data;
      const parsed = Array.isArray(raw) ? raw : (raw as { items?: QcRow[] } | null)?.items ?? [];
      setQcPending(parsed);
      setQcLoading(false);
    });

    safeGet<{ items?: RmRequest[] } | RmRequest[]>('/manufacturing/rm-request').then((r) => {
      const raw = r.data;
      const parsed = Array.isArray(raw) ? raw : (raw as { items?: RmRequest[] } | null)?.items ?? [];
      setRmRequests(parsed);
      setRmLoading(false);
    });

    safeGet<ShiftStats>('/role-kpis/production-operator/shift').then((r) => {
      setStats(r.data);
      setStatsLoading(false);
    });
  }, []);

  const getSku = (w: ShiftWorkOrder) => w.productName ?? w.product?.name ?? w.sku ?? 'Unnamed SKU';
  const getDone = (w: ShiftWorkOrder) => w.completedQty ?? w.done ?? 0;
  const getStation = (w: ShiftWorkOrder) =>
    typeof w.workstation === 'string' ? w.workstation : w.workstation?.name ?? 'Unassigned';
  const getDue = (w: ShiftWorkOrder) =>
    w.dueLabel ?? (w.dueDate ? new Date(w.dueDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—');

  const unitsDone = stats?.unitsDone ?? wos.reduce((acc, w) => acc + getDone(w), 0);
  const unitsTarget = stats?.unitsTarget ?? wos.reduce((acc, w) => acc + (w.quantity ?? 0), 0);
  const shiftPct = unitsTarget > 0 ? (unitsDone / unitsTarget) * 100 : 0;

  return (
    <div
      className="aura-root"
      style={{ padding: 20, background: 'var(--ivory-0)', minHeight: '100%' }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: 14, gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              color: 'var(--ink-3)',
              textTransform: 'uppercase',
              letterSpacing: 0.08,
              marginBottom: 3,
            }}
          >
            Production Operator · Shop floor
          </div>
          <h2 style={{ fontFamily: 'var(--ff-serif)', fontSize: 24, fontWeight: 500 }}>
            Namaste, {firstName}
          </h2>
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            {todayLabel()} · {wos.length} work order{wos.length === 1 ? '' : 's'} assigned
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {onSeeTeam && (
          <button
            className="btn btn-outline btn-sm"
            onClick={onSeeTeam}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Users size={12} /> See team view
          </button>
        )}
      </div>

      {/* Big scan card — BUG-16 fix: whole card is clickable, not just the button */}
      <div
        className="card"
        role="button"
        tabIndex={0}
        onClick={() => navigate('/manufacturing/barcodes')}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate('/manufacturing/barcodes'); }}
        style={{
          padding: 18,
          marginBottom: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          background: 'var(--ivory-1)',
          cursor: 'pointer',
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 12,
            background: 'var(--ink-0)',
            color: 'var(--ivory-0)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ScanLine size={28} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 500 }}>Scan finished-good barcode</div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            Activates warranty · logs to shift report · triggers QC if sampling rule hits
          </div>
        </div>
        {/* Apr 27 (audit P0) — wired to barcode management. BUG-16: card handles navigation too. */}
        <button
          className="btn"
          onClick={e => { e.stopPropagation(); navigate('/manufacturing/barcodes'); }}
          style={{
            padding: '10px 18px',
            background: 'var(--ink-0)',
            color: 'var(--ivory-0)',
            border: '1px solid var(--ink-0)',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          Start scan
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 14 }}>
        <div>
          {/* My work orders */}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <h3 style={{ fontSize: 13, fontWeight: 500 }}>My work orders</h3>
            <div style={{ flex: 1 }} />
            <button
              className="btn btn-outline btn-sm"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <Plus size={11} /> Raw material request
            </button>
          </div>

          {wosLoading ? (
            <Skeleton h={180} />
          ) : wosErr ? (
            <div className="card" style={{ padding: 14, marginBottom: 18 }}>
              <NoData label="Shift work orders unavailable" />
            </div>
          ) : wos.length === 0 ? (
            <div className="card" style={{ padding: 14, marginBottom: 18 }}>
              <NoData label="No work orders assigned for this shift" />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 18 }}>
              {wos.map((w) => {
                const pri = (w.priority ?? 'med').toLowerCase();
                const accent =
                  pri === 'high'
                    ? 'var(--danger, #B8563B)'
                    : pri === 'med' || pri === 'medium'
                    ? 'var(--saffron-600)'
                    : 'transparent';
                const done = getDone(w);
                const qty = w.quantity ?? 0;
                return (
                  <div
                    key={w.id}
                    className="card"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: 10,
                      borderLeft: `3px solid ${accent}`,
                    }}
                  >
                    <div
                      className="num"
                      style={{
                        width: 44,
                        height: 40,
                        borderRadius: 6,
                        background: 'var(--ivory-2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 10,
                        color: 'var(--ink-3)',
                        flexShrink: 0,
                      }}
                    >
                      {(w.orderNumber ?? w.id).slice(-4)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{getSku(w)}</div>
                      <div
                        style={{
                          fontSize: 11,
                          color: 'var(--ink-3)',
                          display: 'flex',
                          gap: 6,
                          alignItems: 'center',
                          marginTop: 2,
                        }}
                      >
                        <span>{getStation(w)}</span>
                        <span>·</span>
                        <span>Due {getDue(w)}</span>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          marginTop: 6,
                        }}
                      >
                        <Chip>
                          {done}/{qty} units
                        </Chip>
                        <div style={{ width: 100 }}>
                          <Progress value={qty > 0 ? (done / qty) * 100 : 0} h={3} />
                        </div>
                      </div>
                    </div>
                    {/* Apr 27 (audit P0/P1) — wired Spec to WO detail; Scan FG to barcode mgr with WO id. */}
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => navigate(`/manufacturing/orders/${w.id}`)}
                    >
                      Spec
                    </button>
                    <button
                      className="btn btn-sm"
                      onClick={() => navigate(`/manufacturing/barcodes?wo=${encodeURIComponent(w.id)}`)}
                      style={{
                        background: 'var(--ink-0)',
                        color: 'var(--ivory-0)',
                        border: '1px solid var(--ink-0)',
                      }}
                    >
                      Scan FG
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* QC pending */}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <h3 style={{ fontSize: 13, fontWeight: 500 }}>QC checkpoints pending</h3>
            {qcPending.length > 0 && (
              <span style={{ marginLeft: 8 }}>
                <Chip color="var(--danger, #B8563B)" bg="var(--danger-bg, #FBEAE6)">
                  {qcPending.length} hold-point{qcPending.length === 1 ? '' : 's'}
                </Chip>
              </span>
            )}
          </div>
          {qcLoading ? (
            <Skeleton h={100} />
          ) : qcPending.length === 0 ? (
            <div className="card" style={{ padding: 14 }}>
              <NoData label="No QC checkpoints pending" />
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: 'var(--ivory-1)', color: 'var(--ink-3)' }}>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 500 }}>WO</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 500 }}>SKU</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 500 }}>
                      Units
                    </th>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 500 }}>
                      Inspector
                    </th>
                    <th style={{ padding: '8px 10px' }} />
                  </tr>
                </thead>
                <tbody>
                  {qcPending.map((q) => {
                    const insp =
                      q.insp ??
                      q.inspector?.name ??
                      [q.inspector?.firstName, q.inspector?.lastName]
                        .filter(Boolean)
                        .join(' ') ??
                      '—';
                    return (
                      <tr key={q.id} style={{ borderTop: '1px solid var(--line)' }}>
                        <td className="num" style={{ padding: '8px 10px', color: 'var(--ink-3)' }}>
                          {q.wo ?? q.workOrder ?? q.id.slice(-6)}
                        </td>
                        <td style={{ padding: '8px 10px' }}>{q.sku ?? q.productName ?? '—'}</td>
                        <td className="num" style={{ padding: '8px 10px', textAlign: 'right' }}>
                          {formatNum(q.count ?? q.unitsPending ?? 0)}
                        </td>
                        <td style={{ padding: '8px 10px', color: 'var(--ink-3)' }}>{insp}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                          {/* Apr 27 (audit P1) — Pass/Fail navigate to QC page with checkpoint id. */}
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => navigate(`/manufacturing/qc?checkpoint=${encodeURIComponent(q.id)}&action=pass`)}
                          >
                            Pass
                          </button>
                          <button
                            className="btn btn-sm"
                            onClick={() => navigate(`/manufacturing/qc?checkpoint=${encodeURIComponent(q.id)}&action=fail`)}
                            style={{
                              marginLeft: 4,
                              background: 'var(--danger-bg, #FBEAE6)',
                              color: 'var(--danger, #B8563B)',
                            }}
                          >
                            Fail
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Raw material requests */}
          {rmLoading ? null : rmRequests.length > 0 ? (
            <div style={{ marginTop: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <h3 style={{ fontSize: 13, fontWeight: 500 }}>Open raw material requests</h3>
                <span style={{ marginLeft: 8 }}>
                  <Chip>{rmRequests.length}</Chip>
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {rmRequests.slice(0, 4).map((r) => (
                  <div
                    key={r.id}
                    className="card"
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10 }}
                  >
                    <ClipboardCheck size={14} color="var(--ink-3)" />
                    <div style={{ flex: 1, fontSize: 12 }}>{r.material ?? 'Material'}</div>
                    <div className="num" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                      {formatNum(r.qty ?? 0)} units
                    </div>
                    <Chip>{r.status ?? 'pending'}</Chip>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        {/* Right rail */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div className="card" style={{ padding: 16 }}>
            <div className="kpi-label" style={{ marginBottom: 8 }}>
              Shift progress
            </div>
            <div className="num-display" style={{ fontSize: 34, letterSpacing: -0.02 }}>
              {formatNum(unitsDone)}
              <span
                style={{
                  fontSize: 14,
                  color: 'var(--ink-3)',
                  fontFamily: 'var(--ff-sans)',
                  marginLeft: 4,
                }}
              >
                /{formatNum(unitsTarget)} units
              </span>
            </div>
            <div style={{ marginTop: 8 }}>
              <Progress value={shiftPct} />
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 10 }}>
              {shiftPct >= 100 ? 'Target hit' : shiftPct >= 75 ? 'On pace' : 'Catch up needed'}
            </div>
          </div>

          <div className="card" style={{ padding: 14 }}>
            <div className="kpi-label" style={{ marginBottom: 10 }}>
              Shift scorecard
            </div>
            {statsLoading ? (
              <Skeleton h={90} />
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 10,
                  fontSize: 12,
                }}
              >
                <div>
                  <div
                    className="num-display"
                    style={{ fontSize: 18, color: 'var(--forest-600)' }}
                  >
                    {stats?.qcFirstPassPct != null ? `${stats.qcFirstPassPct}%` : '—'}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>QC first-pass</div>
                </div>
                <div>
                  <div className="num-display" style={{ fontSize: 18 }}>
                    {stats?.oee ?? '—'}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>OEE</div>
                </div>
                <div>
                  <div className="num-display" style={{ fontSize: 18 }}>
                    {formatNum(stats?.incidents ?? 0)}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>Incidents</div>
                </div>
                <div>
                  <div className="num-display" style={{ fontSize: 18 }}>
                    {stats?.idleMinutes != null ? `${stats.idleMinutes}m` : '—'}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>Idle time</div>
                </div>
              </div>
            )}
          </div>

          {/* Apr 27 (audit P0) — wired to End-of-Shift report page. */}
          <button
            className="btn"
            onClick={() => navigate('/manufacturing/shift-report')}
            style={{
              padding: '12px 16px',
              fontSize: 13,
              background: 'var(--ink-0)',
              color: 'var(--ivory-0)',
              border: '1px solid var(--ink-0)',
              borderRadius: 8,
              fontWeight: 500,
            }}
          >
            Submit shift report · EOS
          </button>
        </div>
      </div>

      <style>{`@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
    </div>
  );
}

export default ProductionOperatorDashboard;
